import {
  type AggregateOp,
  type FieldType,
  groupableKindOf,
  supportsAggregate,
} from '@ofmos/shared/field-types';
import { validateFilterAgainstFields } from '@ofmos/shared/filter-ast';
import type { AggregateRequest, GroupDimension } from '@ofmos/shared/record-group';
import { type OrderBy, RECORD_QUERY_MAX_ORDER_KEYS } from '@ofmos/shared/record-query';
import type { ViewColumnAggregates } from '@ofmos/shared/view';
import type {
  FieldMetadata,
  SavedView,
  SavedViewMode,
  SavedViewPatchInput,
  SavedViewWriteInput,
  ViewFilterNode,
} from '../../../data/client';
import { browserTimeZone } from '../table/groupModel';

/**
 * WHAT A LIST SCREEN IS LOOKING AT (D20), and how a SAVED VIEW turns into it.
 *
 * Everything in this file is pure. The state lift is the riskiest package in the module
 * (open risk 4), and the two things most likely to go wrong - a view that loads into a
 * shape the server would refuse, and a "dirty" indicator that lies - are both decidable
 * without React.
 *
 * THE ARCHIVED SEGMENT IS NOT IN HERE (D15). Sending somebody "the board" is a thing
 * people do; sending somebody "the archive of this object" is not, and a URL that can
 * land a reader in an empty archive they did not ask for is worse than one that always
 * opens on the work. It stays where the admin module put it, and switching views
 * preserves it.
 */
export interface ViewState {
  readonly mode: SavedViewMode;
  /** The STORED form: the ordinary AST plus D17's relative-date operand. */
  readonly filter: ViewFilterNode | null;
  readonly orderBy: readonly OrderBy[] | null;
  /** Empty means "every column", which is what an unsaved list opens as. */
  readonly columnFieldIds: readonly string[];
  readonly boardGroupFieldId: string | null;
  /**
   * THE TABLE'S GROUPING (group-by-aggregation.md D15). Null means the table is a flat
   * list, which is what every view stored before this module and what an unsaved list
   * opens as.
   *
   * The BOARD's dimension stays in `boardGroupFieldId` beside it rather than folding in:
   * a view has exactly one `mode`, so only one of the two is ever read, and folding them
   * would be a data migration over live rows to serve a rename.
   */
  readonly groupBy: readonly GroupDimension[] | null;
  /** The one number beside each group header. Null means "show the row count" (D18). */
  readonly groupAggregate: AggregateRequest | null;
  /** The footer, one operation per column. Null means no footer at all (D18). */
  readonly columnAggregates: ViewColumnAggregates | null;
  /** D29's display toggle. False means empty groups are SHOWN, which is the default. */
  readonly hideEmptyGroups: boolean;
}

/** What a list opens as when no view is selected: everything, unsorted, as a table. */
export const UNSAVED_VIEW: ViewState = {
  mode: 'table',
  filter: null,
  orderBy: null,
  columnFieldIds: [],
  boardGroupFieldId: null,
  groupBy: null,
  groupAggregate: null,
  columnAggregates: null,
  hideEmptyGroups: false,
};

/** One thing a view asked for that the model no longer supports (D18). */
export interface ViewIssue {
  readonly kind: 'column' | 'condition' | 'sort' | 'group';
  /** A complete sentence: it is rendered verbatim in the banner. */
  readonly message: string;
}

export interface ReconciledView {
  readonly state: ViewState;
  readonly issues: readonly ViewIssue[];
}

/**
 * A VIEW NEVER ERRORS. IT DEGRADES, AND SAYS SO (D18).
 *
 * Fields get dropped, archived and retyped, and select options get deleted. A view that
 * 400'd on load would be a view nobody could repair - the screen that repairs it is the
 * one that will not open. So every damaged part is REMOVED and REPORTED, the rest still
 * runs, and nothing is written to the database until the user saves.
 *
 * The rules are not symmetric, deliberately:
 *  - a condition whose FIELD is gone is dropped, because it cannot be evaluated;
 *  - a condition whose select OPTION is gone is KEPT, because it evaluates to "matches
 *    nothing", and that is a true answer rather than a broken one.
 *
 * THIS IS `validateFilterAgainstFields`'s FIRST PRODUCTION CALLER. Until now it was
 * referenced only from its own test. It answers "is this condition legal", per condition;
 * the drop-and-report layer is written here on top of it rather than as a second
 * validator (INV-6).
 */
export function reconcileView(
  view: SavedView,
  fields: readonly FieldMetadata[],
  /** D19/D27: the field ids that may anchor a related filter on THIS object. */
  anchors?: ReadonlySet<string> | undefined,
): ReconciledView {
  const byId = new Map(fields.map((field) => [field.id, field]));
  const fieldTypes: ReadonlyMap<string, FieldType> = new Map(
    fields.map((field) => [field.id, field.type]),
  );
  const issues: ViewIssue[] = [];

  const columnFieldIds = view.columnFieldIds.filter((fieldId) => byId.has(fieldId));
  const droppedColumns = view.columnFieldIds.length - columnFieldIds.length;
  if (droppedColumns > 0) {
    issues.push({
      kind: 'column',
      message: plural(
        droppedColumns,
        'One column was removed because its field no longer exists.',
        `${droppedColumns} columns were removed because their fields no longer exist.`,
      ),
    });
  }

  const filterResult = pruneFilter(view.filter, fieldTypes, anchors);
  if (filterResult.dropped > 0) {
    issues.push({
      kind: 'condition',
      message: plural(
        filterResult.dropped,
        'One filter was removed because its field no longer exists or no longer supports it.',
        `${filterResult.dropped} filters were removed because their fields no longer exist or no longer support them.`,
      ),
    });
  }

  const sortResult = pruneOrderBy(view.orderBy, byId);
  if (sortResult.droppedFields > 0) {
    issues.push({
      kind: 'sort',
      message: plural(
        sortResult.droppedFields,
        'One sort was removed because its field cannot be sorted on any more.',
        `${sortResult.droppedFields} sorts were removed because their fields cannot be sorted on any more.`,
      ),
    });
  }
  if (sortResult.truncated) {
    issues.push({
      kind: 'sort',
      // The server enforces four (`RECORD_QUERY_MAX_ORDER_KEYS`); a fifth is a 400.
      message: `This view sorted by more than ${RECORD_QUERY_MAX_ORDER_KEYS} fields, so only the first ${RECORD_QUERY_MAX_ORDER_KEYS} are used.`,
    });
  }

  const group = resolveBoardGroupField(view.boardGroupFieldId, fields);
  if (view.boardGroupFieldId !== null && group !== view.boardGroupFieldId) {
    issues.push({
      kind: 'group',
      message:
        group === null
          ? 'The board grouped by a field that is gone, and this object has no other select field to group by.'
          : 'The board grouped by a field that is gone, so it falls back to the first select field.',
    });
  }

  const grouping = pruneGrouping(view, byId);
  issues.push(...grouping.issues);

  return {
    state: {
      mode: view.mode,
      filter: filterResult.node,
      orderBy: sortResult.orderBy,
      columnFieldIds,
      boardGroupFieldId: group,
      groupBy: grouping.groupBy,
      groupAggregate: grouping.groupAggregate,
      columnAggregates: grouping.columnAggregates,
      hideEmptyGroups: view.hideEmptyGroups,
    },
    issues,
  };
}

interface PrunedGrouping {
  readonly groupBy: readonly GroupDimension[] | null;
  readonly groupAggregate: AggregateRequest | null;
  readonly columnAggregates: ViewColumnAggregates | null;
  readonly issues: readonly ViewIssue[];
}

/**
 * D8's THIRD RULE, and it is the one an earlier draft of that decision would have hidden:
 * a STORED reference the READER did not write is DROPPED on load.
 *
 * The refuse/omit split everywhere else in this module turns on whether a reference is a
 * property of the REQUEST or of the DATA. A saved view is neither. User A groups a shared
 * view by a field, user B may not read that field, and B named nothing - so refusing would
 * hand B a permanent 403 on a view that loaded, with no way to fix it short of editing
 * somebody else's view. It degrades instead, exactly as a deleted field does, and nothing
 * is written until the user saves (obligations 29 and 30).
 *
 * IN THE BROWSER THOSE TWO CASES ARE ONE CASE, and saying so is what keeps this honest:
 * `useFields` returns the fields the READER may see, so a field denied to B is simply
 * absent from the list this function is handed - the same absence a deleted field leaves.
 * The server asserts the two halves separately, where a `FieldAccess` can be substituted;
 * here there is one lookup and it answers both.
 *
 * A TYPE CHANGE IS THE SECOND WAY IN. A grouping whose field is now ungroupable is
 * dropped, and so is one whose field changed SHAPE - a stored value dimension on a field
 * that is now a date needs a granularity it does not carry, and the server would refuse
 * the read. Repairing it by inventing a granularity would silently change what the view
 * means; a view that loads into a shape the server would refuse is the failure this whole
 * function exists to prevent.
 */
function pruneGrouping(view: SavedView, byId: ReadonlyMap<string, FieldMetadata>): PrunedGrouping {
  const issues: ViewIssue[] = [];

  const stored = view.groupBy;
  const kept =
    stored === null ? [] : stored.filter((dimension) => isLegalDimension(dimension, byId));
  const groupBy =
    stored === null || kept.length !== stored.length ? null : stored.map(readerTimeZone);
  if (stored !== null && groupBy === null) {
    issues.push({
      kind: 'group',
      message:
        'This view grouped the table by a field that is gone or can no longer be grouped, so the table is not grouped any more.',
    });
  }

  const aggregate = view.groupAggregate;
  const groupAggregate = aggregate !== null && isLegalAggregate(aggregate, byId) ? aggregate : null;
  if (aggregate !== null && groupAggregate === null) {
    issues.push({
      kind: 'group',
      message:
        'The number shown beside each group was removed because its field is gone or no longer supports that calculation.',
    });
  }

  const columns = view.columnAggregates;
  const legalColumns =
    columns === null
      ? null
      : Object.fromEntries(
          Object.entries(columns).filter(([fieldId, op]) =>
            isLegalAggregate({ op, fieldId }, byId),
          ),
        );
  const droppedColumns =
    columns === null || legalColumns === null
      ? 0
      : Object.keys(columns).length - Object.keys(legalColumns).length;
  if (droppedColumns > 0) {
    issues.push({
      kind: 'group',
      message: plural(
        droppedColumns,
        'One column total was removed because its field is gone or no longer supports that calculation.',
        `${droppedColumns} column totals were removed because their fields are gone or no longer support those calculations.`,
      ),
    });
  }

  return {
    groupBy,
    groupAggregate,
    // An object that lost every entry is no footer at all, not an empty footer row.
    columnAggregates:
      legalColumns === null || Object.keys(legalColumns).length === 0 ? null : legalColumns,
    issues,
  };
}

function isLegalDimension(
  dimension: GroupDimension,
  byId: ReadonlyMap<string, FieldMetadata>,
): boolean {
  const field = byId.get(dimension.fieldId);
  if (field === undefined) return false;
  const kind = groupableKindOf(field.type);
  if (kind === 'none') return false;
  // A bucket needs its granularity and a plain value dimension must not carry one: both
  // halves are what the server checks, so both are what a stored dimension has to survive.
  return (kind === 'bucketed') === (dimension.granularity !== undefined);
}

function isLegalAggregate(
  request: { readonly op: AggregateOp; readonly fieldId?: string | undefined },
  byId: ReadonlyMap<string, FieldMetadata>,
): boolean {
  // `count` is the group's own row count and names no field, so there is nothing to lose.
  if (request.op === 'count') return request.fieldId === undefined;
  if (request.fieldId === undefined) return false;
  const field = byId.get(request.fieldId);
  return field !== undefined && supportsAggregate(field.type, request.op);
}

/**
 * THE READER'S OWN TIMEZONE, STAMPED ON LOAD (D13, and Arif's answer to question 1).
 *
 * The zone travels from the client on every grouped read, with no server default and no
 * fallback, so two people in two countries see two groupings of one saved view - which is
 * correct, and is what a stored per-view zone would get wrong the moment somebody travels.
 * Stamping it HERE rather than substituting it at read time means the state, the request
 * and what a save would store are one value rather than three, and a stored zone nobody
 * reads never survives to confuse the next reader.
 *
 * It does not make the view dirty: the baseline is this same reconciled state.
 */
function readerTimeZone(dimension: GroupDimension): GroupDimension {
  if (dimension.timeZone === undefined) return dimension;
  return { ...dimension, timeZone: browserTimeZone() };
}

/**
 * The board's group field, resolved the way `KanbanView` already resolves it: the stored
 * one if it is still a live SELECT, otherwise the first SELECT field, otherwise nothing.
 *
 * Exported because the board needs the same answer for an UNSAVED list, and two
 * implementations of "which column set is this board" is how a board and its view
 * disagree about what a drop writes.
 */
export function resolveBoardGroupField(
  preferred: string | null,
  fields: readonly FieldMetadata[],
): string | null {
  const selects = fields.filter((field) => field.type === 'SELECT');
  if (preferred !== null && selects.some((field) => field.id === preferred)) return preferred;
  return selects.toSorted((a, b) => a.position - b.position)[0]?.id ?? null;
}

interface PrunedFilter {
  readonly node: ViewFilterNode | null;
  readonly dropped: number;
}

function pruneFilter(
  node: ViewFilterNode | null,
  fieldTypes: ReadonlyMap<string, FieldType>,
  /**
   * D19: THE LEGAL ANCHOR SET, from `GET .../filter-anchors` (D27).
   *
   * A third parameter rather than a check against `fieldTypes`, because a REVERSE anchor
   * is a field of the CHILD object and is not in that map at all - so an unmodified
   * `pruneFilter` would false-drop every reverse-anchored view on every load. `undefined`
   * means the set has not been read yet, and a related node is then KEPT: keeping leaves
   * the list filtered and, if the anchor really is gone, the server refuses loudly;
   * dropping would widen the list silently, which is the direction this file exists to
   * avoid.
   */
  anchors?: ReadonlySet<string> | undefined,
): PrunedFilter {
  if (node === null) return { node: null, dropped: 0 };

  /**
   * THE RELATED ARM, WITH THREE JOBS RATHER THAN ONE (D19, C2).
   *
   * Before it existed, an EXISTENCE-ONLY related node - the module's headline shape -
   * took the group branch below, whose loop does not execute, hit `children.length === 0`
   * and returned `{node: null, dropped: 0}`. `reconcileView` only pushes a `condition`
   * issue when `dropped > 0`, so there was no chip, no "1 filter was removed" line and no
   * count: the list rendered UNFILTERED and the build was green.
   *
   * The whole-node rule is deliberate. `related(anchor, any, [stage eq open])` losing
   * `stage` must not become "has at least one deal", a chip that says something the user
   * never asked for. Between two widenings, choose the one the user can SEE - the chip
   * disappears and `reconcileView` prints the count.
   */
  if (node.kind === 'related') {
    /**
     * THE ANCHOR IS THE ONLY PART THE BROWSER CAN CHECK, and that is a decided asymmetry
     * rather than an omission. `fieldTypes` is built from THIS object's fields; every
     * inner condition inside a hop names a field of ANOTHER object, so validating them
     * here would report "unknown fieldId" for every correct related view ever saved.
     * A view cannot be SAVED with a bad inner field (the write-time gate resolves per
     * hop), dropping a field is a destructive metadata action requiring a confirm token,
     * and when it does happen the server refuses loudly.
     */
    const anchorLegal = anchors === undefined || anchors.has(node.anchorFieldId);
    return anchorLegal ? { node, dropped: 0 } : { node: null, dropped: 1 };
  }

  if (node.kind === 'condition') {
    /**
     * The shared validator, per condition. It answers three of D18's rows at once - an
     * unknown field, an operator the field's CURRENT type does not support, and a
     * nullary operator carrying a value - which is exactly why this module gives it its
     * first caller instead of writing a fourth check.
     */
    const issues = validateFilterAgainstFields(
      node.value === undefined
        ? { kind: 'condition', fieldId: node.fieldId, operator: node.operator }
        : { kind: 'condition', fieldId: node.fieldId, operator: node.operator, value: node.value },
      fieldTypes,
    );
    return issues.length === 0 ? { node, dropped: 0 } : { node: null, dropped: 1 };
  }

  const children: ViewFilterNode[] = [];
  let dropped = 0;
  for (const child of node.children) {
    const pruned = pruneFilter(child, fieldTypes, anchors);
    dropped += pruned.dropped;
    if (pruned.node !== null) children.push(pruned.node);
  }
  // A group that lost every child is not an empty AND (which matches everything and
  // would silently widen the view) - it is no filter at all.
  if (children.length === 0) return { node: null, dropped };
  /**
   * A group that lost all but ONE child collapses to that child, and this is not
   * cosmetic: `toViewFilter` produces a bare condition for a single row, so a
   * one-child group here would make a freshly loaded view differ from the same view
   * re-applied - and `isDirty` would report "unsaved changes" on a page nobody touched.
   * A NEGATED group never collapses: `not(a)` is not `a`.
   */
  if (children.length === 1 && node.negate !== true) {
    return { node: children[0] as ViewFilterNode, dropped };
  }
  return {
    node: {
      kind: 'group',
      combinator: node.combinator,
      children,
      ...(node.negate === undefined ? {} : { negate: node.negate }),
    },
    dropped,
  };
}

interface PrunedOrderBy {
  readonly orderBy: readonly OrderBy[] | null;
  readonly droppedFields: number;
  readonly truncated: boolean;
}

/**
 * `order_by`'s LEGAL DOMAIN (D15), enforced on the way out as well as on the way in.
 *
 * The query planner throws `not_supported` for EVERY relation kind and for MULTI_SELECT,
 * and refuses more than four keys with `invalid_state`. A field can become either of
 * those after a view was saved - which is precisely the case the write-time check cannot
 * cover.
 */
function pruneOrderBy(
  orderBy: readonly OrderBy[] | null,
  byId: ReadonlyMap<string, FieldMetadata>,
): PrunedOrderBy {
  if (orderBy === null || orderBy.length === 0) {
    return { orderBy: null, droppedFields: 0, truncated: false };
  }
  const legal = orderBy.filter((key) => {
    const field = byId.get(key.fieldId);
    return field !== undefined && isSortableType(field.type);
  });
  const truncated = legal.length > RECORD_QUERY_MAX_ORDER_KEYS;
  const kept = truncated ? legal.slice(0, RECORD_QUERY_MAX_ORDER_KEYS) : legal;
  return {
    orderBy: kept.length === 0 ? null : kept,
    droppedFields: orderBy.length - legal.length,
    truncated,
  };
}

export function isSortableType(type: FieldType): boolean {
  return type !== 'RELATION' && type !== 'MULTI_SELECT';
}

/** The write body for "Save as new view". `objectId` and `name` come from the dialog. */
export function toViewWrite(
  objectId: string,
  name: string,
  state: ViewState,
  isDefault: boolean,
): SavedViewWriteInput {
  return {
    objectId,
    name,
    mode: state.mode,
    filter: state.filter,
    orderBy: state.orderBy === null ? null : [...state.orderBy],
    columnFieldIds: [...state.columnFieldIds],
    boardGroupFieldId: state.boardGroupFieldId,
    ...groupingWrite(state),
    isDefault,
  };
}

/**
 * The four grouping members, spelled once and spread into both write bodies (D15). Two
 * schemas that each list them is how one of them ends up a member short, which is the
 * shape `columnFieldIds` already has on the server for the same reason.
 */
function groupingWrite(state: ViewState) {
  return {
    groupBy: state.groupBy === null ? null : state.groupBy.map((dimension) => ({ ...dimension })),
    groupAggregate: state.groupAggregate === null ? null : { ...state.groupAggregate },
    columnAggregates: state.columnAggregates === null ? null : { ...state.columnAggregates },
    hideEmptyGroups: state.hideEmptyGroups,
  };
}

/**
 * The PATCH body for "save into the view I am looking at".
 *
 * `objectId` is deliberately absent, and not by omission: `savedViewPatchSchema` is a
 * `strictObject` that does not accept it, because moving a view to another object would
 * invalidate its columns, its filter, its sort and its board group field at once. That
 * is a new view, not an edit - which is why this is a separate function rather than
 * `toViewWrite` with a field deleted.
 */
export function toViewPatch(state: ViewState): SavedViewPatchInput {
  return {
    mode: state.mode,
    filter: state.filter,
    orderBy: state.orderBy === null ? null : [...state.orderBy],
    columnFieldIds: [...state.columnFieldIds],
    boardGroupFieldId: state.boardGroupFieldId,
    ...groupingWrite(state),
  };
}

/**
 * Has the user changed anything since the view loaded?
 *
 * Compared on the RECONCILED state rather than on the stored row, and that is the point:
 * a view whose broken half was just dropped IS different from what is stored, and saying
 * so is what turns D18's banner into an offer ("Save the view to keep this") rather than
 * a notice nobody can act on.
 *
 * `JSON.stringify` is honest here because every part of the state is plain JSON with a
 * fixed key order produced by this file - there is no `Date`, no `undefined` and no map.
 */
export function isDirty(state: ViewState, baseline: ViewState): boolean {
  return JSON.stringify(state) !== JSON.stringify(baseline);
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}
